import type {
  AlarmUpdateDto,
  AlarmCreateDto,
  AssetUpdateDto,
  AssetCreateDto,
  AssetGroupUpdateDto,
  AssetGroupCreateDto,
  BeaconUpdateDto,
  BeaconCreateDto,
  ContactListUpdateDto,
  ContactListCreateDto,
  CustomerUpdateDto,
  CustomerCreateDto,
  CustomerAlarmCenterRelationUpdateDto,
  CustomerAlarmCenterRelationCreateDto,
  CustomerUserRelationCreateDto,
  DeviceUpdateDto,
  DeviceCreateDto,
  ObservationTriggerUpdateDto,
  ObservationTriggerCreateDto,
  OverlayUpdateDto,
  OverlayCreateDto,
  StaticResponderUpdateDto,
  StaticResponderCreateDto,
  UserUpdateDto,
  UserCreateDto,
  WebhookUpdateDto,
  WebhookCreateDto,
  ZoneUpdateDto,
  ZoneCreateDto,
} from '@x-guard/xgac-node-main-api';

type RefName = 'Asset' | 'AssetGroup' | 'ContactList' | 'Zone';

const toRef = (value: any, refName: RefName) => {

  if (!value) return value;
  if (typeof value === 'string') return { _id: value, _ref: refName };
  return { _id: value._id, _ref: value._ref || refName };

};

const refArrayToIds = (values?: any[]) => (values || []).map((value) => value?._id || value);

const idsToRefArray = (values: any[] | undefined, refName: RefName) => (values || []).map((value) => toRef(value, refName));

const targetObservationTriggerConditionTypes = ['constraint/asset', 'constraint/assetGroup', 'constraint/zone'];

const normalizeObservationTriggerActionForForm = (action: any) => {

  if (action?.type === 'sms' || action?.type === 'email') {

    return {
      ...action,
      config: {
        ...action.config,
        toRaw: action.config?.toRaw || [],
        toContactList: refArrayToIds(action.config?.toContactList),
        toTriggerUser: Boolean(action.config?.toTriggerUser),
      },
    };

  }
  return action;

};

const normalizeObservationTriggerActionForApi = (action: any) => {

  if (action?.type === 'alarm') {

    return {
      type: action.type,
      config: {
        severity: action.config?.severity,
        type: action.config?.type,
        procedure: action.config?.procedure || null,
        name: action.config?.name,
      },
    };

  }
  if (action?.type === 'sms') {

    return {
      type: action.type,
      config: {
        toRaw: action.config?.toRaw || [],
        toContactList: idsToRefArray(action.config?.toContactList, 'ContactList'),
        toTriggerUser: Boolean(action.config?.toTriggerUser),
        message: action.config?.message,
      },
    };

  }
  if (action?.type === 'email') {

    return {
      type: action.type,
      config: {
        toRaw: action.config?.toRaw || [],
        toContactList: idsToRefArray(action.config?.toContactList, 'ContactList'),
        toTriggerUser: Boolean(action.config?.toTriggerUser),
        message: action.config?.message,
        subject: action.config?.subject,
        ...(action.config?.locale ? { locale: action.config.locale } : {}),
      },
    };

  }
  if (action?.type === 'assetUpdate') {

    return {
      type: action.type,
      config: {
        ...(
          action.config?.available !== undefined && action.config?.available !== null && action.config.available !== ''
            ? { available: action.config.available }
            : {}
        ),
        ...(action.config?.app?.mode ? { app: { mode: action.config.app.mode } } : {}),
      },
    };

  }
  return action;

};

const normalizeObservationTriggerFilterForForm = (filter: any): any => {

  if (!filter) return filter;
  if (filter.type === 'operator/and' || filter.type === 'operator/or') {

    return {
      ...filter,
      operands: (filter.operands || []).map(normalizeObservationTriggerFilterForForm),
    };

  }
  if (filter.type === 'operator/not') {

    return {
      ...filter,
      operand: normalizeObservationTriggerFilterForForm(filter.operand),
    };

  }
  if (filter.type === 'constraint/asset') {

    return {
      ...filter,
      assets: refArrayToIds(filter.assets),
    };

  }
  if (filter.type === 'constraint/assetGroup') {

    return {
      ...filter,
      groups: refArrayToIds(filter.groups),
    };

  }
  if (filter.type === 'constraint/zone') {

    return {
      ...filter,
      zone: filter.zone?._id || filter.zone,
    };

  }
  return filter;

};

const normalizeObservationTriggerFilterForApi = (filter: any): any => {

  if (!filter) return filter;
  if (filter.type === 'operator/and' || filter.type === 'operator/or') {

    return {
      type: filter.type,
      operands: (filter.operands || []).map(normalizeObservationTriggerFilterForApi),
    };

  }
  if (filter.type === 'operator/not') {

    return {
      type: filter.type,
      operand: normalizeObservationTriggerFilterForApi(filter.operand),
    };

  }
  if (filter.type === 'constraint/asset') {

    return {
      type: filter.type,
      assets: idsToRefArray(filter.assets, 'Asset'),
    };

  }
  if (filter.type === 'constraint/assetGroup') {

    return {
      type: filter.type,
      groups: idsToRefArray(filter.groups, 'AssetGroup'),
    };

  }
  if (filter.type === 'constraint/zone') {

    return {
      type: filter.type,
      zone: toRef(filter.zone, 'Zone'),
    };

  }
  if (filter.type === 'constraint/schedule') {

    return {
      type: filter.type,
      schedule: filter.schedule,
    };

  }
  return filter;

};

const isObservationTriggerScheduleFilter = (filter: any) => filter?.type === 'constraint/schedule';

const isObservationTriggerTargetCondition = (filter: any) => targetObservationTriggerConditionTypes.includes(filter?.type);

const getSingleNegatedObservationTriggerTargetCondition = (filter: any) => {

  if (
    filter?.type === 'operator/or'
    && filter.operands?.length === 1
    && filter.operands[0]?.type === 'operator/not'
    && isObservationTriggerTargetCondition(filter.operands[0].operand)
  ) {

    return filter.operands[0].operand;

  }
  return null;

};

const isEditableObservationTriggerCondition = (condition: any) => (
  isObservationTriggerTargetCondition(condition)
  || (condition?.type === 'operator/not' && isObservationTriggerTargetCondition(condition.operand))
  || Boolean(getSingleNegatedObservationTriggerTargetCondition(condition))
);

const hasUnsupportedObservationTriggerFilter = (filter: any): boolean => {

  if (!filter || isObservationTriggerScheduleFilter(filter) || isEditableObservationTriggerCondition(filter)) return false;
  if (filter.type === 'operator/and') {

    return (filter.operands || []).some(hasUnsupportedObservationTriggerFilter);

  }
  return true;

};

const normalizeObservationTriggerMatchConditionForForm = (condition: any) => {

  const normalizedCondition = normalizeObservationTriggerFilterForForm(condition);
  const negatedTargetCondition = getSingleNegatedObservationTriggerTargetCondition(normalizedCondition);
  if (negatedTargetCondition) {

    return {
      ...negatedTargetCondition,
      match: 'doesNotMatch',
    };

  }
  if (normalizedCondition?.type === 'operator/not') {

    return {
      ...normalizedCondition.operand,
      match: 'doesNotMatch',
    };

  }
  return {
    ...normalizedCondition,
    match: 'matches',
  };

};

const getObservationTriggerFilterFormValues = (filter: any) => {

  if (!filter) {

    return {
      filterConditionBlockType: 'operator/and',
      filterConditions: [],
      filterSchedules: { entries: [] },
    };

  }

  const normalizedFilter = normalizeObservationTriggerFilterForForm(filter);
  const operands = normalizedFilter.type === 'operator/and' ? normalizedFilter.operands || [] : [normalizedFilter];
  const scheduleOperands = operands.filter(isObservationTriggerScheduleFilter);
  let conditionOperands = operands.filter((operand: any) => !isObservationTriggerScheduleFilter(operand));
  let filterConditionBlockType = 'operator/and';
  const filterHasUnsupportedConditions = conditionOperands
    .some((operand: any) => !isEditableObservationTriggerCondition(operand));

  if (
    !filterHasUnsupportedConditions
    && conditionOperands.length === 1
    && conditionOperands[0]?.type === 'operator/not'
    && conditionOperands[0].operand?.type === 'operator/or'
  ) {

    filterConditionBlockType = 'operator/not';
    conditionOperands = conditionOperands[0].operand.operands || [];

  }

  return {
    filterConditionBlockType,
    filterConditions: filterHasUnsupportedConditions ? [] : conditionOperands.map(normalizeObservationTriggerMatchConditionForForm),
    filterHasUnsupportedConditions: hasUnsupportedObservationTriggerFilter(normalizedFilter),
    filterSchedules: {
      entries: scheduleOperands.flatMap((operand: any) => operand.schedule?.entries || []),
    },
  };

};

export const normalizeObservationTriggerForForm = (trigger: any) => ({
  ...trigger,
  actions: trigger.actions?.map(normalizeObservationTriggerActionForForm) || [],
  filter: normalizeObservationTriggerFilterForForm(trigger.filter),
  ...getObservationTriggerFilterFormValues(trigger.filter),
});

const normalizeObservationTriggerForApi = (trigger: any) => {

  const {
    filterConditions,
    filterConditionBlockType,
    filterSchedules,
    ...payload
  } = trigger;
  delete payload.filterHasUnsupportedConditions;

  return {
    ...payload,
    ...(typeof trigger.status === 'boolean' ? { status: trigger.status ? 'active' : 'inactive' } : {}),
    ...(trigger.actions ? { actions: trigger.actions.map(normalizeObservationTriggerActionForApi) } : {}),
    ...(trigger.filter ? { filter: normalizeObservationTriggerFilterForApi(trigger.filter) } : {}),
  };

};

export const toXgacUpdateDto = (patchData: any, recordName: string) => {

  const returnData = { ...patchData };
  switch (recordName) {

  case 'alarms':
    return returnData as AlarmUpdateDto;
  case 'assets':
    return returnData as AssetUpdateDto;
  case 'asset-groups':
    return returnData as AssetGroupUpdateDto;
  case 'beacons':
    return returnData as BeaconUpdateDto;
  case 'contact-lists':
    return returnData as ContactListUpdateDto;
  case 'customers':
    return returnData as CustomerUpdateDto;
  case 'customer-alarm-center-relations':
    return returnData as CustomerAlarmCenterRelationUpdateDto;
  case 'devices':
    return returnData as DeviceUpdateDto;
  case 'observation-triggers':
    return normalizeObservationTriggerForApi(returnData) as ObservationTriggerUpdateDto;
  case 'overlays':
    return returnData as OverlayUpdateDto;
  case 'staticResponders':
    return returnData as StaticResponderUpdateDto;
  case 'users':
    return returnData as UserUpdateDto;
  case 'webhooks':
    return returnData as WebhookUpdateDto;
  case 'zones':
    return returnData as ZoneUpdateDto;
  default:
    return returnData;

  }

};

export const toXgacCreateDto = (data: any, recordName: string) => {

  const returnData = { ...data };
  switch (recordName) {

  case 'alarms':
    return returnData as AlarmCreateDto;
  case 'assets':
    return returnData as AssetCreateDto;
  case 'asset-groups':
    return returnData as AssetGroupCreateDto;
  case 'beacons':
    return returnData as BeaconCreateDto;
  case 'contact-lists':
    return returnData as ContactListCreateDto;
  case 'customers':
    return returnData as CustomerCreateDto;
  case 'customer-alarm-center-relations':
    return returnData as CustomerAlarmCenterRelationCreateDto;
  case 'customer-user-relations':
    return {
      customer: { _id: returnData.customer._id, _ref: 'Customer' },
      user: { _id: returnData['user._id'], _ref: 'User' },
      assetGroupRestrictions: returnData.assetGroupRestrictions?.map((assetGroup: string) => ({ _id: assetGroup, _ref: 'AssetGroup' })),
      roles: returnData.roles,
    } as CustomerUserRelationCreateDto;
  case 'devices':
    return returnData as DeviceCreateDto;
  case 'observation-triggers':
    return normalizeObservationTriggerForApi(returnData) as ObservationTriggerCreateDto;
  case 'overlays':
    return returnData as OverlayCreateDto;
  case 'staticResponders':
    return returnData as StaticResponderCreateDto;
  case 'users':
    return returnData as UserCreateDto;
  case 'webhooks':
    return returnData as WebhookCreateDto;
  case 'zones':
    return returnData as ZoneCreateDto;
  default:
    return returnData;

  }

};
